// src/containers/Receptions/ReceptionList/components/SubordersTable.tsx
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useFieldArray, useWatch } from "react-hook-form";
import { ExpandMoreRounded } from "@mui/icons-material";
import { Text } from "$components";
import Button from "$components/Button";
import ProductSearchInput, {
  getProductFieldMatch,
} from "$forms/ProductSearchInput";
import useReception from "$hooks/useReception";
import { ProjectServices, DockyardServices } from "$services";
import ReceptionStatus from "$types/ReceptionStatus";
import { SuborderType, SUBORDER_TYPE_LABELS } from "$types";
import { queryClient } from "$utils/queryClient";
import SuborderTable from "./SuborderTable";
import { CacheInvalidationAction } from "$utils/cache";

type Props = {
  nestIndex: any;
  control: any;
  register: any;
  getValues: any;
  setValue: any;
  watch: any;
  errors: any;
  reception: any;
  updateOrderConformity: (
    orderIndex: number,
    suborderIndex: number,
    productIndex: number,
    value: boolean
  ) => void;
  currentProductQuantityIndex: (
    orderIdex: number,
    suborderIndex: number,
    suborderProductIndex: number
  ) => number;
  currentProductQuantityIndexInOrder: (
    orderIdex: number,
    suborderIndex: number,
    suborderProduct: any
  ) => number;
  totalUnconform: number;
  setTotalUnconform: (totalUnconform: number) => void;
  filtered: boolean;
  isReadOnly?: boolean;
};

// Memoize product row to prevent unnecessary re-renders
const ProductRow = memo(
  ({
    orderIndex,
    suborderIndex,
    pIndex,
    // product,
    control,
    register,
    setValue,
    errors,
    isReadOnly,
    reception,
    updateOrderConformity,
    sumUnconformProduct,
    lineProductPath,
    conformity,
    productName,
    productUnit,
    productReference,
    productQuantityValue,
    lineProductIndex,
    suborder,
  }: {
    orderIndex: number;
    suborderIndex: number;
    pIndex: number;
    // product: any;
    control: any;
    register: any;
    setValue: any;
    errors: any;
    isReadOnly: boolean;
    reception: any;
    updateOrderConformity: any;
    currentProductQuantityIndex: any;
    sumUnconformProduct: any;
    suborderTypeLabel: string;
    lineProductPath: string;
    conformity: boolean | null;
    productName: string;
    productUnit: string;
    productReference: string;
    productQuantityValue: string;
    lineProductIndex: number;
    suborder: any;
  }) => {
    const getName = (property) =>
      `orders[${orderIndex}].suborders[${suborderIndex}].suborderProducts[${pIndex}].${property}`;

    return (
      <React.Fragment
        key={`product-row-${orderIndex}-${suborderIndex}-${pIndex}`}
      >
        <TableRow key={lineProductPath}>
          <TableCell padding="none">
            <Controller
              control={control}
              name={getName("reference")}
              render={({ field }) => (
                <div className="tdPaddingXs">
                  <TextField
                    disabled
                    variant="outlined"
                    {...field}
                    value={productReference}
                  />
                </div>
              )}
            />
          </TableCell>

          <TableCell padding="none">
            <div title={productName}>
              <Controller
                control={control}
                name={getName("name")}
                render={({ field }) => (
                  <div className="tdPaddingXs">
                    <TextField
                      disabled
                      variant="outlined"
                      {...field}
                      value={productName}
                      style={{ width: "100%" }}
                    />
                  </div>
                )}
              />
            </div>
          </TableCell>

          <TableCell padding="none">
            <div title={productUnit}>
              <Controller
                control={control}
                name={getName("unit")}
                render={({ field }) => (
                  <div className="tdPaddingXs">
                    <TextField
                      disabled
                      variant="outlined"
                      {...field}
                      value={productUnit}
                      style={{ width: "100%" }}
                    />
                  </div>
                )}
              />
            </div>
          </TableCell>

          <TableCell padding="none">
            <Controller
              control={control}
              name={getName("quantity")}
              render={({ field }) => (
                <div className="tdPaddingXs">
                  <TextField
                    disabled
                    variant="outlined"
                    {...field}
                    value={productQuantityValue || "0"}
                    error={
                      errors.productQuantity !== undefined &&
                      errors.productQuantity[lineProductIndex] !== undefined &&
                      errors.productQuantity[lineProductIndex]
                        .receivedQuantity !== undefined
                    }
                  />
                </div>
              )}
            />
          </TableCell>

          <TableCell padding="none">
            <div className="tdPaddingXs">
              <Controller
                control={control}
                name={`${lineProductPath}.alreadyReceivedQuantity`}
                render={({ field }) => (
                  <TextField
                    disabled
                    variant="outlined"
                    {...field}
                    value={field.value || 0}
                  />
                )}
              />
            </div>
          </TableCell>
          <TableCell padding="none">
            <div className="tdPaddingXs">
              <Controller
                control={control}
                name={`${lineProductPath}.receivedQuantity`}
                render={({ field }) => (
                  <TextField
                    disabled={
                      isReadOnly ||
                      !!reception?.receptionReferences?.some((reception) => {
                        return reception.suborderReferences?.some((sub) => {
                          return sub === suborder.reference;
                        });
                      })
                    }
                    variant="outlined"
                    {...field}
                    value={field.value || 0}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                    }}
                    error={
                      errors.productQuantity !== undefined &&
                      errors.productQuantity[lineProductIndex] !== undefined &&
                      errors.productQuantity[lineProductIndex]
                        .receivedQuantity !== undefined
                    }
                  />
                )}
              />
            </div>
          </TableCell>

          <TableCell padding="none">
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <input
                type="hidden"
                name={`${lineProductPath}.conformity`}
                {...register(`${lineProductPath}.conformity`)}
              />

              <Button
                disabled={isReadOnly}
                color="secondary"
                style={{ minWidth: 40, marginRight: 8 }}
                onClick={() => {
                  if (!conformity) {
                    setValue(`${lineProductPath}.unconformQuantity`, 0);
                    sumUnconformProduct();
                  }
                  const newValue = !conformity;
                  setValue(`${lineProductPath}.conformity`, newValue);
                  updateOrderConformity(
                    orderIndex,
                    suborderIndex,
                    pIndex,
                    newValue
                  );
                }}
                sx={{
                  border: "none !important",
                  color: "rgba(0, 0, 0) !important",
                  "&:hover": {
                    color: "rgba(0, 0, 0) !important",
                    backgroundColor: "rgba(0, 0, 0, 0.04) !important",
                  },
                  "&:disabled": {
                    color: "rgba(0, 0, 0, 0.26) !important",
                  },
                  "& svg": {
                    color: "inherit !important",
                  },
                }}
              >
                {conformity === false ? (
                  <div style={{ color: "tomato" }}>
                    <SentimentVeryDissatisfiedIcon />
                  </div>
                ) : (
                  <div style={{ color: "green" }}>
                    <SentimentSatisfiedAltIcon />
                  </div>
                )}
              </Button>

              {conformity === false && (
                <Controller
                  control={control}
                  name={`${lineProductPath}.unconformQuantity`}
                  render={({ field }) => (
                    <TextField
                      disabled={isReadOnly}
                      variant="outlined"
                      {...field}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                      }}
                      onBlur={sumUnconformProduct}
                      error={
                        errors.productQuantity !== undefined &&
                        errors.productQuantity[lineProductIndex] !==
                          undefined &&
                        errors.productQuantity[lineProductIndex]
                          .unconformQuantity !== undefined
                      }
                    />
                  )}
                />
              )}
            </div>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  }
);

function SubordersTable({
  nestIndex,
  control,
  register,
  getValues,
  setValue,
  watch,
  errors,
  reception,
  totalUnconform,
  updateOrderConformity,
  currentProductQuantityIndex,
  currentProductQuantityIndexInOrder,
  setTotalUnconform,
  filtered,
  isReadOnly = false,
}: Props) {
  const { fields: suborders } = useFieldArray({
    control,
    name: `orders[${nestIndex}].suborders`,
  });
  const { classes } = useReception();
  const [sorted, setSorted] = useState(false);

  const orderedSuborders = useMemo(() => {
    return suborders
      .map((suborder: any, index: number) => ({
        suborder,
        index,
        reference: (suborder as any)?.reference ?? "",
      }))
      .sort((a, b) =>
        String(a.reference).localeCompare(String(b.reference), undefined, {
          numeric: true,
          sensitivity: "base",
        })
      );
  }, [suborders]);

  // Use useWatch at the component level only
  const productQuantity = useWatch({
    control,
    name: "productQuantity",
    defaultValue: [],
  });

  const orders = useWatch({
    control,
    name: "orders",
    defaultValue: [],
  });

  // Watch all relevant conformity fields
  const allProductQuantityFields = useWatch({
    control,
    name: "productQuantity",
  });

  const sumUnconformProduct = useCallback(() => {
    let total = 0;
    if (productQuantity) {
      productQuantity.forEach((qty: any) => {
        const number = Number(qty.unconformQuantity);
        if (!Number.isNaN(number)) {
          total += number;
        }
      });
    }
    setTotalUnconform(total);
  }, [productQuantity, setTotalUnconform]);

  useEffect(() => {
    sumUnconformProduct();
  }, [sumUnconformProduct]);

  const [productReference_tfilter, setProductReference_tfilter] = useState("");
  const [productDesignation_tfilter, setProductDesignation_tfilter] =
    useState("");
  const [project, setProject] = useState(null);
  const [isFilteredByProducts, setIsFilteredByProducts] = useState(false);
  const [dockyard, setDockyard] = useState(null);

  useEffect(() => {
    if (productDesignation_tfilter !== "" || productReference_tfilter !== "") {
      setIsFilteredByProducts(true);
    } else {
      setIsFilteredByProducts(false);
    }
  }, [
    setIsFilteredByProducts,
    productDesignation_tfilter,
    productReference_tfilter,
  ]);

  const matchToProductFilters = useCallback(
    (productReference, productName) => {
      return (
        getProductFieldMatch(productReference, productReference_tfilter) &&
        getProductFieldMatch(productName, productDesignation_tfilter)
      );
    },
    [productReference_tfilter, productDesignation_tfilter]
  );

  const fetchProjects = useCallback(async () => {
    const { project: projectId } = suborders?.[0] as any;
    if (projectId) {
      let getProject;
      getProject = await queryClient.getQueryData([
        CacheInvalidationAction.GET_PROJECT_BY_ID,
        projectId,
      ]);
      if (!getProject) {
        getProject = await queryClient.fetchQuery({
          queryKey: [CacheInvalidationAction.GET_PROJECT_BY_ID, projectId],
          queryFn: () => ProjectServices.getProjectById(projectId),
          staleTime: 1000 * 60,
        });
      }
      setProject(getProject);
    }
  }, [suborders, setProject]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const fetchDockyards = useCallback(async () => {
    const { dockyard: dockyardId } = suborders[0] as any;
    if (dockyardId) {
      let getDockyard = await queryClient.getQueryData([
        CacheInvalidationAction.GET_DOCKYARD_BY_ID,
        dockyardId,
      ]);
      if (!getDockyard) {
        getDockyard = await queryClient.fetchQuery({
          queryKey: [CacheInvalidationAction.GET_DOCKYARD_BY_ID, dockyardId],
          queryFn: () => DockyardServices.getDockyardById(dockyardId),
          staleTime: 1000 * 60,
        });
      }
      setDockyard(getDockyard);
    }
  }, [suborders, setDockyard]);

  useEffect(() => {
    fetchDockyards();
  }, [fetchDockyards]);

  const getOrderLabel = useCallback(() => {
    const suborderType = (suborders[0] as any)?.type;
    const suborderDockyard =
      suborderType === SuborderType.DOCKYARD ? dockyard : null;
    const suborderProject =
      suborderType === SuborderType.PROJECT ? project : null;
    const suborderTypeLabel =
      suborderType === SuborderType.DOCKYARD
        ? `Chantier : [${suborderDockyard?.code}] ${suborderDockyard?.name}`
        : suborderType === SuborderType.PROJECT
          ? `Projet : [${suborderProject?.code}] ${suborderProject?.name}`
          : SUBORDER_TYPE_LABELS[SuborderType.STOCK];

    return suborderTypeLabel;
  }, [suborders, dockyard, project]);

  const clearReceivedValues = useCallback(() => {
    if (reception.status === ReceptionStatus.TODO && !isReadOnly) {
      const orderReference = orders[nestIndex]?.reference;

      productQuantity.forEach((item, i) => {
        if (item.orderReference === orderReference) {
          setValue(`productQuantity[${i}].receivedQuantity`, "0");
        }
      });
    }
  }, [
    reception.status,
    isReadOnly,
    orders,
    nestIndex,
    productQuantity,
    setValue,
  ]);

  const fillReceivedWithExpectedQuantities = useCallback(() => {
    if (reception.status === ReceptionStatus.TODO && !isReadOnly) {
      const orderReference = orders[nestIndex]?.reference;
      const order = orders[nestIndex];

      productQuantity.forEach((item, i) => {
        if (item.orderReference !== orderReference) return;
        const suborder = order?.suborders?.find(
          (so: any) => so.reference === item.suborderReference
        );
        const suborderProduct = suborder?.suborderProducts?.find(
          (p: any) => p.reference === item.productId
        );
        if (suborderProduct == null) return;
        const expected = suborderProduct.quantity ?? 0;
        setValue(`productQuantity[${i}].receivedQuantity`, String(expected));
      });
    }
  }, [
    reception.status,
    isReadOnly,
    orders,
    nestIndex,
    productQuantity,
    setValue,
  ]);

  const uniqueOrderTypeLabel = useMemo(() => getOrderLabel(), [getOrderLabel]);

  // Precompute product data for filtering and rendering
  const productData = useMemo(() => {
    const result = [];
    suborders.forEach((suborder, suborderIndex) => {
      const suborderProducts = (suborder as any).suborderProducts || [];

      suborderProducts.forEach((product, pIndex) => {
        const productReference = product.reference || "";
        const productName = product.name || "";
        const productUnit = product.unit || "";
        const productQuantity = product.quantity || "";

        const lineProductIndex = currentProductQuantityIndex(
          nestIndex,
          suborderIndex,
          pIndex
        );

        const lineProductPath = `productQuantity[${lineProductIndex}]`;
        const conformity =
          allProductQuantityFields?.[lineProductIndex]?.conformity ?? true;

        result.push({
          suborderIndex,
          pIndex,
          product,
          productReference,
          productName,
          productUnit,
          productQuantity,
          lineProductIndex,
          lineProductPath,
          conformity,
        });
      });
    });

    return result;
  }, [
    suborders,
    currentProductQuantityIndex,
    allProductQuantityFields,
    nestIndex,
  ]);

  // Memoize the filtered products
  const filteredProducts = useMemo(() => {
    let result = [...productData];

    // Apply text filters
    if (isFilteredByProducts) {
      result = result.filter((item) =>
        matchToProductFilters(item.productReference, item.productName)
      );
    }

    // Apply sorting
    if (sorted) {
      result.sort((a, b) => {
        return a.productName
          .toLowerCase()
          .localeCompare(b.productName.toLowerCase());
      });
    } else {
      result.sort((a, b) => {
        const aIndex = currentProductQuantityIndexInOrder(
          nestIndex,
          a.suborderIndex,
          a.product
        );
        const bIndex = currentProductQuantityIndexInOrder(
          nestIndex,
          b.suborderIndex,
          b.product
        );
        return aIndex - bIndex;
      });
    }

    return result;
  }, [
    productData,
    isFilteredByProducts,
    matchToProductFilters,
    sorted,
    currentProductQuantityIndexInOrder,
    nestIndex,
  ]);

  return filtered ? (
    <div>
      {orderedSuborders.map(({ suborder, index: suborderIndex }) => (
        <div
          key={suborder.id ?? `suborder-${nestIndex}-${suborderIndex}`}
          style={{ marginTop: 15, marginBottom: 45 }}
        >
          <SuborderTable
            orderIndex={nestIndex}
            suborderIndex={suborderIndex}
            isReadOnly={isReadOnly}
            {...{
              control,
              register,
              getValues,
              setValue,
              watch,
              errors,
              reception,
              totalUnconform,
              updateOrderConformity,
              currentProductQuantityIndex,
              currentProductQuantityIndexInOrder,
              setTotalUnconform,
              filtered,
            }}
          />
        </div>
      ))}
    </div>
  ) : (
    <>
      {suborders.length === 1 && (
        <div>
          <p>{`${uniqueOrderTypeLabel}`}</p>
          <div style={{ marginBottom: 10 }}>
            <Switch
              checked={sorted}
              onChange={() => {
                setSorted(!sorted);
              }}
              name="showCompany"
              color="primary"
            />
            <Text type="text">Trier par désignation</Text>
          </div>
        </div>
      )}
      <Accordion
        slotProps={{
          transition: {
            unmountOnExit: true,
          },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreRounded />}>
          Liste des produits
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer>
            <Table>
              <TableHead className={classes.topFormTable}>
                <TableRow>
                  <TableCell padding="none" style={{ minWidth: 100 }}>
                    <div className={classes.productsTableCellHead}>
                      <ProductSearchInput
                        value={productReference_tfilter}
                        onChange={(e: any) => {
                          setProductReference_tfilter(e.target.value);
                        }}
                        mode="reference"
                      />
                    </div>
                  </TableCell>
                  <TableCell padding="none" style={{ minWidth: 280 }}>
                    <div className={classes.productsTableCellHead}>
                      <ProductSearchInput
                        value={productDesignation_tfilter}
                        onChange={(e: any) => {
                          setProductDesignation_tfilter(e.target.value);
                        }}
                        mode="designation"
                      />
                    </div>
                  </TableCell>
                  <TableCell padding="none" style={{ minWidth: 80 }}>
                    <div className={classes.productsTableCellHead}>Unité</div>
                  </TableCell>
                  <TableCell padding="none" style={{ width: 70 }}>
                    <div className={classes.productsTableCellHead}>
                      Qté
                      <small style={{ marginTop: 4 }}>attendue</small>
                    </div>
                  </TableCell>
                  <TableCell padding="none" style={{ width: 70 }}>
                    <div className={classes.productsTableCellHead}>
                      Qté
                      <small style={{ marginTop: 4 }}>déjà livrée</small>
                    </div>
                  </TableCell>
                  <TableCell padding="none" style={{ width: 70 }}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        flexWrap: "nowrap",
                        gap: 4,
                      }}
                    >
                      <div
                        className={classes.productsTableCellHead}
                        style={{ flexShrink: 0 }}
                      >
                        Qté
                        <small style={{ marginTop: 4 }}>reçue</small>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 2,
                          flexShrink: 0,
                        }}
                      >
                        <Button
                          color="secondary"
                          title="Renseigner la quantité reçue avec la quantité attendue"
                          disabled={
                            isReadOnly ||
                            reception.status !== ReceptionStatus.TODO
                          }
                          sx={{
                            minWidth: "32px !important",
                            padding: "4px !important",
                            border: "none !important",
                            color: "rgba(0, 0, 0) !important",
                            "&:hover": {
                              color: "rgba(0, 0, 0) !important",
                              backgroundColor: "rgba(0, 0, 0, 0.04) !important",
                            },
                            "&:disabled": {
                              color: "rgba(0, 0, 0, 0.26) !important",
                            },
                            "& svg": {
                              color: "inherit !important",
                            },
                          }}
                          onClick={fillReceivedWithExpectedQuantities}
                        >
                          <span
                            style={{
                              fontSize: 18,
                              fontWeight: 600,
                              lineHeight: 1,
                            }}
                          >
                            =
                          </span>
                        </Button>
                        <Button
                          color="secondary"
                          disabled={
                            isReadOnly ||
                            reception.status !== ReceptionStatus.TODO
                          }
                          sx={{
                            minWidth: "32px !important",
                            padding: "4px !important",
                            border: "none !important",
                            color: "rgba(0, 0, 0) !important",
                            "&:hover": {
                              color: "rgba(0, 0, 0) !important",
                              backgroundColor: "rgba(0, 0, 0, 0.04) !important",
                            },
                            "&:disabled": {
                              color: "rgba(0, 0, 0, 0.26) !important",
                            },
                            "& svg": {
                              color: "inherit !important",
                            },
                          }}
                          onClick={clearReceivedValues}
                        >
                          <ClearRoundedIcon style={{ width: 18 }} />
                        </Button>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell padding="none" style={{ width: 100 }}>
                    <div className={classes.productsTableCellHead}>
                      Conformité
                    </div>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody className={classes.bodyFormTable}>
                {isFilteredByProducts && filteredProducts.length === 0 && (
                  <TableRow>
                    <TableCell padding="none" colSpan={7}>
                      <div style={{ textAlign: "center", padding: 20 }}>
                        Aucun produit correspondant.
                      </div>
                    </TableCell>
                  </TableRow>
                )}

                {filteredProducts.map((item) => {
                  const suborder =
                    orders[nestIndex]?.suborders?.[item.suborderIndex] || {};
                  const suborderType = suborder.type;
                  const suborderDockyard =
                    suborderType === SuborderType.DOCKYARD ? dockyard : null;
                  const suborderProject =
                    suborderType === SuborderType.PROJECT ? project : null;

                  const suborderTypeLabel =
                    suborderType === SuborderType.DOCKYARD
                      ? `Chantier : [${suborderDockyard?.code || "N/A"}] ${
                          suborderDockyard?.name || "Non spécifié"
                        }`
                      : suborderType === SuborderType.PROJECT
                        ? `Projet : [${suborderProject?.code || "N/A"}] ${
                            suborderProject?.name || "Non spécifié"
                          }`
                        : SUBORDER_TYPE_LABELS[SuborderType.STOCK];

                  return (
                    <ProductRow
                      key={`product-${nestIndex}-${item.suborderIndex}-${item.pIndex}`}
                      orderIndex={nestIndex}
                      suborderIndex={item.suborderIndex}
                      pIndex={item.pIndex}
                      // product={item.product}
                      control={control}
                      register={register}
                      setValue={setValue}
                      errors={errors}
                      isReadOnly={isReadOnly}
                      reception={reception}
                      updateOrderConformity={updateOrderConformity}
                      currentProductQuantityIndex={currentProductQuantityIndex}
                      sumUnconformProduct={sumUnconformProduct}
                      suborderTypeLabel={suborderTypeLabel}
                      lineProductPath={item.lineProductPath}
                      conformity={item.conformity}
                      productName={item.productName}
                      productUnit={item.productUnit}
                      productReference={item.productReference}
                      productQuantityValue={item.productQuantity}
                      lineProductIndex={item.lineProductIndex}
                      suborder={suborder}
                    />
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
    </>
  );
}

export default memo(SubordersTable);
